import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { welcomeWeekService } from '@/services/welcomeWeekService';
import { organizationService } from '@/services/organizationService';
import { DEFAULT_WW_SEASON_CODE, type WelcomeWeekGroupFeed } from '@/types/welcomeWeek';
import { buildFullviewEditUrl, buildFullviewUrl, eventDescription, formatEventTime, locationDisplayHtml, seasonQuery } from './wwUi';
import { calendarHtml } from '@/utils/safeHtml';
import { ArrowLeft, CalendarDays, ExternalLink, MapPin, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import GroupEventEditor from './GroupEventEditor';

export default function GroupPublicPage() {
  const { slug, groupCode } = useParams<{ slug: string; groupCode: string }>();
  const [searchParams] = useSearchParams();
  const seasonCode = searchParams.get('season') || DEFAULT_WW_SEASON_CODE;
  const explicitSeason = searchParams.get('season');
  const [feed, setFeed] = useState<WelcomeWeekGroupFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  // Owners/admins land on the welcome-week list without being redirected; every
  // other viewer (plain members, group-admins of a single group) gets forwarded
  // by WelcomeWeekPublic straight back to their group, so for them "back" must
  // skip the list and go up to the org page to avoid a redirect loop.
  const [isOrgManager, setIsOrgManager] = useState(false);
  const [editPin, setEditPin] = useState<string | null>(null);
  // The iframe is held back until we know whether the viewer gets the editable
  // fullview: rendering it optimistically flashed the read-only view for a
  // split second before the pin lookup swapped the src to the edit URL.
  const [editResolved, setEditResolved] = useState(false);

  const load = useCallback(async () => {
    if (!slug || !groupCode) return;
    setLoading(true);
    try {
      const data = await welcomeWeekService.getPublicGroupFeed(slug, groupCode, seasonCode);
      setFeed(data);
    } catch (error) {
      console.error('[GroupPublic] load failed', error);
      setFeed(null);
    } finally {
      setLoading(false);
    }
  }, [slug, groupCode, seasonCode]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if ((event.data?.type === 'omega-action-success' || event.data?.type === 'ww-action-success')) {
        console.log('[GroupPublic] received success message from iframe, syncing + refreshing feed');
        // The iframe action (Refresh from <org> / Push to all groups) updated the
        // CalOne side; mirror the current master into Supabase before reloading
        // so the mobile-friendly list reflects it, not the stale snapshot.
        void (async () => {
          await welcomeWeekService.syncWwMaster(slug, seasonCode);
          await load();
        })();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [load, slug, seasonCode]);

  useEffect(() => {
    let cancelled = false;
    setEditResolved(false);
    (async () => {
      const orgId = feed?.organization?.id;
      const groupId = feed?.group?.id;
      const seasonId = feed?.season?.id;
      if (!orgId || !groupId) {
        if (!cancelled) {
          setCanEdit(false);
          setIsOrgManager(false);
          setEditResolved(true);
        }
        return;
      }
      try {
        const memberships = await organizationService.myMemberships();
        const m = memberships.find((x) => x.organization_id === orgId);
        const orgManager = m?.role === 'owner' || m?.role === 'admin';
        if (!cancelled) setIsOrgManager(orgManager);
        if (orgManager) {
          // canEdit -> the pin effect below finishes the resolution.
          if (!cancelled) setCanEdit(true);
          return;
        }
        const adminGroups = await welcomeWeekService.listMyAdminGroups(orgId, seasonId);
        const groupAdmin = adminGroups.some((g) => g.id === groupId);
        if (!cancelled) {
          setCanEdit(groupAdmin);
          // Read-only viewer: nothing more to wait for. (If groupAdmin, the
          // pin effect resolves once the pin is fetched.)
          if (!groupAdmin) setEditResolved(true);
        }
      } catch {
        if (!cancelled) {
          setCanEdit(false);
          setIsOrgManager(false);
          setEditResolved(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [feed?.organization?.id, feed?.group?.id, feed?.season?.id]);

  // For authorized admins, fetch this group's edit pin so the embedded fullview
  // can be opened in editable mode. Pins are gated server-side (RLS + RPC).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const groupKey = (feed?.group?.slug || feed?.group?.code || '').toLowerCase();
      if (!canEdit || !slug || !groupKey) {
        if (!cancelled) setEditPin(null);
        return;
      }
      try {
        const access = await welcomeWeekService.getMyEditAccess(slug, seasonCode);
        if (!cancelled) setEditPin(access.groupPins[groupKey] || null);
      } catch {
        if (!cancelled) setEditPin(null);
      } finally {
        if (!cancelled) setEditResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEdit, slug, seasonCode, feed?.group?.slug, feed?.group?.code]);



  return (
    <PublicLayout>
      <div className="container py-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CalendarDays className="h-10 w-10 animate-pulse text-muted-foreground" />
          </div>
        ) : !feed?.group || !feed.season || !feed.organization ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarDays className="h-14 w-14 mx-auto text-muted-foreground" />
              <h1 className="mt-4 text-2xl font-semibold">Group schedule not found</h1>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="space-y-3">
                <Button asChild variant="ghost" className="px-0">
                  <Link
                    to={
                      isOrgManager
                        ? `/o/${feed.organization.slug}/ww${seasonQuery(explicitSeason)}`
                        : `/o/${feed.organization.slug}`
                    }
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {feed.organization.name}
                  </Link>
                </Button>
                <div className="flex items-center gap-2">
                  <Badge>{feed.group.code}</Badge>
                  <Badge variant="secondary">{feed.season.code}</Badge>
                </div>
                <h1 className="text-3xl font-bold tracking-tight">{feed.group.name}</h1>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button asChild>
                  <a
                    href={`https://www.calone.com/${feed.organization.slug}/${feed.group.slug || feed.group.code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Group Welcome Week Calendar
                  </a>
                </Button>
              </div>
            </div>

            {(() => {
              const fullviewSlug = feed.group.slug || feed.group.code;
              const editUrl = editPin ? buildFullviewEditUrl(fullviewSlug, editPin, slug) : '';
              const iframeSrc = editUrl || buildFullviewUrl(fullviewSlug, slug);
              const editable = Boolean(editUrl);
              if (!editResolved) {
                // Hold the iframe until we know which fullview (read-only vs
                // editable) the viewer gets, so admins don't see the read-only
                // view flash before the editable one loads.
                return (
                  <Card className="overflow-hidden">
                    <div className="flex items-center justify-center" style={{ height: 949 }}>
                      <CalendarDays className="h-10 w-10 animate-pulse text-muted-foreground" />
                    </div>
                  </Card>
                );
              }
              return (
                <Card className="overflow-hidden">
                  <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      {editable ? <Pencil className="h-4 w-4" /> : <CalendarDays className="h-4 w-4" />}
                      {editable ? 'Edit week view' : 'Full week view'}
                      {editable && (
                        <Badge variant="secondary" className="ml-1">Editing</Badge>
                      )}
                    </div>
                    <Button asChild variant="ghost" size="sm">
                      <a href={iframeSrc} target="_blank" rel="noopener noreferrer">
                        Open in new window
                        <ExternalLink className="h-3.5 w-3.5 ml-1.5 opacity-70" />
                      </a>
                    </Button>
                  </div>
                  <iframe
                    title={`${feed.group.name} ${editable ? 'editable ' : ''}full week view`}
                    src={iframeSrc}
                    className="w-full bg-background"
                    style={{ height: 949, border: 0 }}
                    loading="lazy"
                  />
                </Card>
              );
            })()}

            {editPin ? (
              <GroupEventEditor
                season={feed.season.code}
                code={feed.group.code}
                slug={feed.group.slug || feed.group.code}
                pin={editPin}
              />
            ) : (
            <div className="grid gap-3">
              {(feed.events || []).map((event) => {
                const locationHtml = locationDisplayHtml(event);
                const description = eventDescription(event);
                return (
                  <Card key={event.id}>
                    <CardContent className="p-5">
                      <div className="space-y-3">
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div>
                            <h2 className="font-semibold">{event.title}</h2>
                            <p className="text-sm text-muted-foreground">{formatEventTime(event.starts_at, event.ends_at)}</p>
                          </div>
                          <div className="flex items-start gap-3 md:max-w-sm">
                            {locationHtml && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <MapPin className="h-4 w-4 shrink-0" />
                                <span
                                  className="min-w-0 break-words [&_a]:underline [&_a]:text-primary"
                                  dangerouslySetInnerHTML={calendarHtml(locationHtml)}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        {description && (
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{description}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}
